import { useState, useMemo } from 'react';
import { Icon } from '@ascend/shared/components';
import {
  PaginationControls,
  SearchBar,
  usePagination,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../components/ListControls';

// ─── Types ────────────────────────────────────────────────────────────────────

type TxType = 'deposit' | 'withdrawal' | 'borrow' | 'repayment';

interface Transaction {
  id: string;
  type: TxType;
  token: string;
  amount: string;
  timestamp: string;
  txHash: string;
  date: Date;
}

type TypeFilter = 'all' | TxType;
type DateFilter = 'all' | '7' | '30' | '90';

// ─── Deterministic RNG ────────────────────────────────────────────────────────

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// ─── Mock Data Generation ─────────────────────────────────────────────────────

const COLLATERAL_TOKENS = ['ABN', 'SNG', 'FMM', 'APX', 'MSM', 'SBC'];
const SETTLEMENT_TOKENS = ['USDC', 'USDT', 'USD1'];
const TX_TYPES: TxType[] = ['deposit', 'withdrawal', 'borrow', 'repayment'];

function padTwo(n: number) {
  return String(n).padStart(2, '0');
}

function formatDate(d: Date): string {
  return `${padTwo(d.getDate())}/${padTwo(d.getMonth() + 1)}/${d.getFullYear()}, ${padTwo(d.getHours())}:${padTwo(d.getMinutes())}:${padTwo(d.getSeconds())}`;
}

function formatAmount(n: number): string {
  return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function generateTransactions(): Transaction[] {
  const txs: Transaction[] = [];
  const startMs = new Date(2025, 0, 1).getTime();
  const endMs = new Date(2026, 2, 20).getTime();
  const range = endMs - startMs;

  for (let i = 0; i < 45; i++) {
    const s = i + 1;
    const type = TX_TYPES[Math.floor(seededRandom(s * 7) * TX_TYPES.length)];
    const isCollateralTx = type === 'deposit' || type === 'withdrawal';
    const tokenPool = isCollateralTx ? COLLATERAL_TOKENS : SETTLEMENT_TOKENS;
    const token = tokenPool[Math.floor(seededRandom(s * 13) * tokenPool.length)];
    const rawAmt = Math.floor(seededRandom(s * 17) * 4999900 + 100) / 100;
    const sign = type === 'deposit' || type === 'repayment' ? '-' : '+';
    const amount = `${sign}${formatAmount(rawAmt)} ${token}`;
    const date = new Date(startMs + Math.floor(seededRandom(s * 23) * range));
    const h1 = Math.floor(seededRandom(s * 31) * 0xffff)
      .toString(16)
      .padStart(4, '0');
    const h2 = Math.floor(seededRandom(s * 37) * 0xffff)
      .toString(16)
      .padStart(4, '0');

    txs.push({
      id: `tx-${s}`,
      type,
      token,
      amount,
      timestamp: formatDate(date),
      txHash: `0x${h1}...${h2}`,
      date,
    });
  }

  return txs.sort((a, b) => b.date.getTime() - a.date.getTime());
}

const TRANSACTIONS = generateTransactions();

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

// ─── Page Component ───────────────────────────────────────────────────────────

export function HistoryPage() {
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [search, setSearch] = useState('');
  const { page, setPage, pageSize, setPageSize, paginate } = usePagination(
    TABLE_PAGE_SIZE_OPTIONS[0],
  );

  const handleTypeChange = (v: TypeFilter) => {
    setTypeFilter(v);
    setPage(1);
  };
  const handleDateChange = (v: DateFilter) => {
    setDateFilter(v);
    setPage(1);
  };
  const handleSearch = (v: string) => {
    setSearch(v);
    setPage(1);
  };

  const filteredTransactions = useMemo(() => {
    let result = TRANSACTIONS;

    if (typeFilter !== 'all') {
      result = result.filter((t) => t.type === typeFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const days = parseInt(dateFilter, 10);
      const cutoff = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
      result = result.filter((t) => t.date >= cutoff);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (t) =>
          t.token.toLowerCase().includes(q) || t.txHash.toLowerCase().includes(q),
      );
    }

    return result;
  }, [typeFilter, dateFilter, search]);

  const { paginated, totalItems } = paginate(filteredTransactions);

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:interface-time-clock-circle-clock-loading-measure-time-circle" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Transaction History</h1>
          <p className="text-sm text-muted-foreground">
            View your past deposits, borrows, and repayments.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <select
          value={typeFilter}
          onChange={(e) => handleTypeChange(e.target.value as TypeFilter)}
          className={`${INPUT_CLASS} sm:w-52`}
        >
          <option value="all">All Transactions</option>
          <option value="deposit">Deposits</option>
          <option value="withdrawal">Withdrawals</option>
          <option value="borrow">Borrows</option>
          <option value="repayment">Repayments</option>
        </select>
        <select
          value={dateFilter}
          onChange={(e) => handleDateChange(e.target.value as DateFilter)}
          className={`${INPUT_CLASS} sm:w-44`}
        >
          <option value="all">All dates</option>
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="90">Last 90 days</option>
        </select>
        <div className="sm:ml-auto">
          <SearchBar
            value={search}
            onChange={handleSearch}
            placeholder="Search token or tx hash…"
            className="w-full sm:w-64"
          />
        </div>
      </div>

      {/* Transaction list */}
      <div className="space-y-3">
        {paginated.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-12 text-center">
            <p className="text-sm text-muted-foreground">No transactions found.</p>
          </div>
        ) : (
          paginated.map((tx) => (
            <div
              key={tx.id}
              className="rounded-xl border border-border p-4 hover:bg-accent/30 transition-colors"
            >
              {/* Desktop layout */}
              <div className="hidden sm:flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <TxIcon type={tx.type} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {TX_LABEL[tx.type]} {tx.token}
                    </p>
                    <p className="text-xs text-muted-foreground">{tx.timestamp}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                  <span
                    className={`text-sm font-semibold ${tx.amount.startsWith('+') ? 'text-ascend-success' : 'text-foreground'}`}
                  >
                    {tx.amount}
                  </span>
                  <span className="font-mono text-xs text-muted-foreground">
                    {tx.txHash}
                  </span>
                  <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                    Confirmed
                  </span>
                </div>
              </div>

              {/* Mobile layout */}
              <div className="sm:hidden space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TxIcon type={tx.type} />
                    <span className="text-sm font-medium">
                      {TX_LABEL[tx.type]} {tx.token}
                    </span>
                  </div>
                  <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                    Confirmed
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{tx.timestamp}</span>
                  <span
                    className={`font-semibold ${tx.amount.startsWith('+') ? 'text-ascend-success' : 'text-foreground'}`}
                  >
                    {tx.amount}
                  </span>
                </div>
                <p className="font-mono text-xs text-muted-foreground">{tx.txHash}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      <PaginationControls
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItems={totalItems}
        label="transactions"
        pageSizeOptions={TABLE_PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}

// ─── Helper Components ────────────────────────────────────────────────────────

const TX_LABEL: Record<TxType, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  borrow: 'Borrow',
  repayment: 'Repayment',
};

const TX_ICON_CONFIG: Record<
  TxType,
  { iconName: string; bg: string; color: string }
> = {
  deposit: { iconName: 'streamline:interface-arrows-button-to-bottom-arrow-down-line-to-bottom', bg: 'bg-blue-100', color: 'text-blue-600' },
  withdrawal: {
    iconName: 'streamline:interface-arrows-button-to-top-arrow-up-line-to-top',
    bg: 'bg-orange-100',
    color: 'text-orange-600',
  },
  borrow: { iconName: 'streamline:interface-arrows-corner-up-right-keyboard-top-arrow-right-up', bg: 'bg-green-100', color: 'text-green-600' },
  repayment: { iconName: 'streamline:interface-arrows-synchronize-arrows-loading-load-sync-synchronize-arrow-reload', bg: 'bg-purple-100', color: 'text-purple-600' },
};

function TxIcon({ type }: { type: TxType }) {
  const { iconName, bg, color } = TX_ICON_CONFIG[type];
  return (
    <div
      className={`flex h-8 w-8 items-center justify-center rounded-full ${bg} flex-shrink-0`}
    >
      <Icon name={iconName} className={`h-4 w-4 ${color}`} />
    </div>
  );
}
